import { Injectable } from '@angular/core'
import { PermissionStore } from '@engineering11/access-web'
import { catchError, distinctUntilChanged, map, Observable, of, shareReplay, startWith, switchMap } from 'rxjs'
import { Permission } from 'shared-lib'

export type AssistantGate = 'entitled' | 'upsell' | 'loading'

/**
 * Single source of truth for the Cnect Plus entitlement that gates every assistant surface (the bottom
 * command bar and the dock). Reads ONLY entitlement via `PermissionStore` (permission presence, no
 * product/plan-ID comparison — NFR9).
 *
 * Gates on `loaded$` FIRST: `hasPermission` emits `false` synchronously while permissions are still
 * loading, so branching on it directly would flash the upsell at an entitled user during the load window.
 * We hold `'loading'` until `loaded$` is true, then branch. Fails closed: unresolved → loading (never a
 * backend call), error → upsell (never the entitled surface).
 */
@Injectable({ providedIn: 'root' })
export class AssistantGateService {
  readonly gate$: Observable<AssistantGate> = this.permissionStore.loaded$.pipe(
    switchMap(loaded =>
      loaded
        ? this.permissionStore.hasPermission(Permission.AssistantChat).pipe(map(granted => (granted ? 'entitled' : 'upsell') as AssistantGate))
        : of('loading' as const)
    ),
    startWith('loading' as const),
    distinctUntilChanged(),
    catchError(() => of('upsell' as const)),
    shareReplay({ bufferSize: 1, refCount: true })
  )

  constructor(private permissionStore: PermissionStore) {}
}
